// src/features/manual/components/ManualImage.tsx

import React from 'react';
import { useTranslate } from 'react-admin';
import { Box, Skeleton, Typography } from '@mui/material';
import { useManualImage } from '../hooks/useManual';

interface ManualImageProps {
  /** The path the API rewrote the image to (`/api/v1/ui/manual/images/<name>-<lang>`). */
  src: string;
  /** The caption; also the alternative text. */
  alt: string;
}

/**
 * A figure of the manual: a screen capture in a hairline card frame with its caption. The image
 * endpoint needs the session, so the picture is fetched with the token and shown from an object URL,
 * which a click opens at full size.
 */
export const ManualImage: React.FC<ManualImageProps> = ({ src, alt }) => {
  const translate = useTranslate();
  const { url, failed } = useManualImage(src);

  return (
    <Box component="figure" sx={{ m: '8px 0 16px', maxWidth: '100%' }}>
      <Box
        sx={(theme) => ({
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: theme.custom.radius.card,
          overflow: 'hidden',
          bgcolor: 'background.paper',
        })}
      >
        {url ? (
          // Captures are scaled down to the chapter's width (a third of it on a phone): a click opens
          // the picture at full size in a new tab.
          <Box
            component="a"
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            title={translate('manual.image.enlarge')}
            sx={{ display: 'block', cursor: 'zoom-in' }}
          >
            <Box component="img" src={url} alt={alt} sx={{ display: 'block', width: '100%', height: 'auto' }} />
          </Box>
        ) : failed ? (
          <Box role="img" aria-label={alt} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 120, px: 2 }}>
            <Typography variant="caption" color="text.secondary">
              {translate('manual.state.imageMissing')}
            </Typography>
          </Box>
        ) : (
          <Skeleton variant="rectangular" height={200} aria-hidden />
        )}
      </Box>
      {alt && (
        <Typography component="figcaption" variant="caption" sx={(theme) => ({ display: 'block', mt: 1, color: theme.custom.text.tertiary })}>
          {alt}
        </Typography>
      )}
    </Box>
  );
};

export default ManualImage;
